"use client";

import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  listMembers,
  getMember,
  useListMemberSessions as useGeneratedListMemberSessions,
  updateMemberRole,
  suspendMember,
  reactivateMember,
  updateMemberProfile,
  revokeMemberSession,
  listPendingInvitations,
  sendInvitation,
  revokeInvitation,
  resendInvitation,
} from "@/lib/api/generated";
import type {
  InvitationSendRequest,
  ListMembersParams,
  UpdateMemberRoleRequest,
} from "@/lib/api/model";
import { showToast } from "@/components/ui/sonner";

export function useMembers(
  params?: ListMembersParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["org", "members", params],
    queryFn: () => listMembers(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useMember(id: string | undefined) {
  return useQuery({
    queryKey: ["org", "members", id],
    queryFn: () => getMember(id!),
    enabled: !!id,
  });
}

export function useMemberSessions(
  memberId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useGeneratedListMemberSessions(memberId ?? "", {
    query: {
      queryKey: ["org", "members", memberId, "sessions"],
      enabled: !!memberId && (options?.enabled ?? true),
    },
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: "admin" | "member" }) =>
      updateMemberRole(id, { role } as UpdateMemberRoleRequest),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org", "members"] });
      showToast.success("Role updated");
    },
    onError: (err: Error) =>
      showToast.error("Role update failed", { description: err.message }),
  });
}

export function useSuspendMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => suspendMember(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org", "members"] });
      qc.invalidateQueries({ queryKey: ["billing", "seats"] });
      showToast.success("Member deactivated");
    },
    onError: (err: Error) =>
      showToast.error("Deactivation failed", { description: err.message }),
  });
}

export function useReactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivateMember(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org", "members"] });
      qc.invalidateQueries({ queryKey: ["billing", "seats"] });
      showToast.success("Member activated");
    },
    onError: (err: Error) =>
      showToast.error("Activation failed", { description: err.message }),
  });
}

export function useUpdateMemberProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        first_name?: string;
        last_name?: string;
        avatar_url?: string;
        phone?: string;
      };
    }) => updateMemberProfile(id, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["org", "members", vars.id] });
      qc.invalidateQueries({ queryKey: ["org", "members"] });
      showToast.success("Profile updated");
    },
    onError: (err: Error) =>
      showToast.error("Profile update failed", { description: err.message }),
  });
}

export function useRevokeMemberSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      sessionId,
    }: {
      memberId: string;
      sessionId: string;
    }) => revokeMemberSession(memberId, sessionId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ["org", "members", vars.memberId, "sessions"],
      });
      qc.invalidateQueries({ queryKey: ["org", "members", vars.memberId] });
      showToast.success("Session revoked", {
        description: "That device is now signed out.",
      });
    },
    onError: (err: Error) =>
      showToast.error("Session revocation failed", {
        description: err.message,
      }),
  });
}

export function usePendingInvitations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["invitations", "pending"],
    queryFn: () => listPendingInvitations(),
    enabled: options?.enabled ?? true,
  });
}

export function useSendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvitationSendRequest) => sendInvitation(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations", "pending"] });
      qc.invalidateQueries({ queryKey: ["org", "members"] });
    },
    onError: (err: Error) =>
      showToast.error("Invitation failed", { description: err.message }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeInvitation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations", "pending"] });
      showToast.success("Invitation revoked");
    },
    onError: (err: Error) =>
      showToast.error("Revocation failed", { description: err.message }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resendInvitation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations", "pending"] });
      showToast.success("Invitation resent");
    },
    onError: (err: Error) =>
      showToast.error("Resend failed", { description: err.message }),
  });
}
